import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useAuth0 } from '@auth0/auth0-vue'
import * as Sentry from '@sentry/vue'
import { createStoreBase } from '@/presentation/stores/resources/storeBase'
import { UserRole } from '@/core/domain/resources/enums/UserRole'
import { storeEventBus } from '@/presentation/stores/utils/storeEventBus'
import { useAuthentication } from '@/presentation/composables/auth/useAuthentication.ts'

/**
 * Store d'authentification responsable uniquement de l'authentification et des rôles
 * Ne contient pas de logique métier non liée à l'authentification
 */
export const useAuthStore = defineStore('auth', () => {
  // SERVICES & DEPENDENCIES
  const {
    checkSession,
    loginWithRedirect,
    logout,
    idTokenClaims,
    getAccessTokenSilently,
    isAuthenticated: auth0IsAuthenticated,
    isLoading: auth0IsLoading,
    user: userAuth0Ref
  } = useAuth0()

  const { roles, isAdmin, isEduEngineer, isContentApprover, isTeacher, isPerdir, isCpe } =
    useAuthentication()

  // STORE BASE
  const storeBase = createStoreBase(['loginProcess', 'logoutProcess'])

  // STATE
  const authInitialized = ref(false)

  // ACTIONS
  /**
   * Réinitialise l'état du store
   */
  function $reset() {
    storeBase.resetBase()
    authInitialized.value = false
  }

  /**
   * Initialise seulement la partie authentification
   * Ce store ne charge pas les données utilisateur ou les données métier
   */
  async function initAuth() {
    try {
      storeBase.loading.value['loginProcess'] = true
      storeBase.resetError()

      if (!auth0IsAuthenticated.value) {
        return Promise.resolve(false)
      }

      // Marquer l'authentification comme initialisée
      authInitialized.value = true

      // Émettre un événement pour notifier le reste de l'application
      // que l'authentification est complète
      storeEventBus.emit('auth-initialized', {
        authenticated: true
      })

      storeBase.updateLastUpdated()
      return Promise.resolve(true)
    } catch (error) {
      storeBase.setError(error)

      // Émettre un événement d'échec d'authentification
      storeEventBus.emit('auth-failed', {
        error: error instanceof Error ? error.message : String(error)
      })

      return Promise.reject(error)
    } finally {
      storeBase.loading.value['loginProcess'] = false
    }
  }

  /**
   * Initialise le processus de déconnexion
   */
  async function initLogout() {
    try {
      storeBase.loading.value['logoutProcess'] = true
      storeBase.resetError()

      // Vérifier si l'utilisateur est authentifié
      if (!auth0IsAuthenticated.value) {
        console.warn('Utilisateur déjà déconnecté')
        return Promise.resolve()
      }

      // Émettre un événement avant la déconnexion
      storeEventBus.emit('auth-before-logout', {})

      // Faire le logout Auth0 avec gestion d'erreur améliorée
      await logout({
        logoutParams: {
          returnTo: window.location.origin
        },
        openUrl(url: string) {
          // Utiliser l'URL construite si Auth0 ne fournit pas d'URL
          window.location.assign(url)
        }
      })

      // Réinitialiser l'état après un délai pour permettre à Auth0 de finir
      setTimeout(() => {
        $reset()
        storeEventBus.emit('auth-logged-out', {})
      }, 100)

      return Promise.resolve()
    } catch (error) {
      console.error('Erreur détaillée lors du logout:', error)
      Sentry.captureException(error)
      storeBase.setError(error)

      // Même en cas d'erreur, essayer de nettoyer l'état local
      try {
        $reset()
        storeEventBus.emit('auth-logged-out', {})
      } catch (resetError) {
        console.error('Erreur lors du reset après échec de logout:', resetError)
      }

      return Promise.reject(error)
    } finally {
      // Nettoyer les clés localStorage
      try {
        const keysToRemove: string[] = []
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i)
          if (key && key.startsWith('route_')) keysToRemove.push(key)
        }
        keysToRemove.forEach((k) => localStorage.removeItem(k))
      } catch (storageError) {
        console.warn('Erreur lors du nettoyage localStorage:', storageError)
      }

      Sentry.setUser(null)
      storeBase.loading.value['logoutProcess'] = false
    }
  }

  // GETTERS (COMPUTED)
  /**
   * Récupère les rôles de l'utilisateur depuis les claims du token
   */
  const getRoles = computed(() => {
    return roles.value || []
  })

  /**
   * Vérifie si l'utilisateur est autorisé à se connecter (whitelist de rôles)
   */
  const isAllowedToLogIn = computed(() => {
    const rolesWhitelist = [
      UserRole.Values.ADMIN,
      UserRole.Values.EDU_ENGINEER,
      UserRole.Values.PERDIR,
      UserRole.Values.CPE,
      UserRole.Values.TEACHER
    ]
    return getRoles.value.some((role: UserRole.Values) => rolesWhitelist.includes(role))
  })

  /**
   * Vérifie si un utilisateur a un rôle d'backoffice
   */
  const hasBackOfficeRole = computed(() => {
    return UserRole.isBackOfficeRole(getRoles.value as UserRole.Values[])
  })

  /**
   * Vérifie si un utilisateur a un rôle d'établissement
   */
  const hasSchoolRole = computed(() => {
    return UserRole.isSchoolRole(getRoles.value as UserRole.Values[])
  })

  /**
   * Vérifie si l'authentification a été initialisée
   */
  const isAuthInitialized = computed(() => {
    return authInitialized.value
  })

  /**
   * Computed pour l'état d'authentification (déréférencé)
   */
  const isAuthenticated = computed(() => {
    return auth0IsAuthenticated.value
  })

  /**
   * Computed pour l'utilisateur Auth0 (déréférencé)
   */
  const userAuth0 = computed(() => {
    return userAuth0Ref.value
  })

  /**
   * Computed pour le loading combiné
   */
  const isLoading = computed(() => {
    return auth0IsLoading.value || storeBase.isLoading.value
  })

  return {
    // State
    ...storeBase,
    authInitialized,

    // Actions
    $reset,
    initAuth,
    initLogout,
    checkSession,
    loginWithRedirect,
    getAccessTokenSilently,

    // Auth0 properties (computed pour cohérence)
    isAuthenticated,
    idTokenClaims,
    isLoading,
    userAuth0,

    // Getters
    getRoles,
    isAdmin,
    isEduEngineer,
    isContentApprover,
    isTeacher,
    isPerdir,
    isCpe,
    isAllowedToLogIn,
    hasBackOfficeRole,
    hasSchoolRole,
    isAuthInitialized
  }
})

export type AuthStore = ReturnType<typeof useAuthStore>
